export const ACCORDION = {
  init() {
    Accordion.initAll('[data-accordion]')
  },
}

class Accordion {
  static initAll(selector = '[data-accordion]') {
    const roots = document.querySelectorAll(selector)

    roots.forEach(root => {
      if (root.__accordionInstance) return
      root.__accordionInstance = true

      const ins = new Accordion(root)
      ins.init()
    })
  }

  constructor(root) {
    this.root = root
    this.trigger = root.querySelector('[data-accordion-trigger]')
    this.panel = root.querySelector('[data-accordion-panel]')

    this.duration = this._getDuration()
    this._isAnimating = false
    this._tid = 0

    this.isSpOnly = root.hasAttribute('data-accordion-sp-only')
    this.mq = window.matchMedia('(max-width: 768px)')

    this.onClick = this.onClick.bind(this)
    this.onKeydown = this.onKeydown.bind(this)
    this.onMediaChange = this.onMediaChange.bind(this)
    this.onTransitionEnd = this.onTransitionEnd.bind(this)
  }

  init() {
    if (!this.trigger || !this.panel) return

    if (!this.trigger.hasAttribute('tabindex')) this.trigger.setAttribute('tabindex', '0')
    this.trigger.setAttribute('role', 'button')

    if (!this.panel.id) {
      this.panel.id = `acc-panel-${Math.random().toString(16).slice(2)}`
    }
    this.trigger.setAttribute('aria-controls', this.panel.id)

    const isOpen = this.root.getAttribute('data-accordion-open') === 'true'
    this._applyResponsiveState(isOpen, true)

    this.trigger.addEventListener('click', this.onClick)
    this.trigger.addEventListener('keydown', this.onKeydown)
    this.panel.addEventListener('transitionend', this.onTransitionEnd)

    if (this.isSpOnly) {
      if (this.mq.addEventListener) {
        this.mq.addEventListener('change', this.onMediaChange)
      } else {
        this.mq.addListener(this.onMediaChange)
      }
    }
  }

  destroy() {
    if (!this.trigger || !this.panel) return

    this.trigger.removeEventListener('click', this.onClick)
    this.trigger.removeEventListener('keydown', this.onKeydown)
    this.panel.removeEventListener('transitionend', this.onTransitionEnd)

    if (this.isSpOnly) {
      if (this.mq.removeEventListener) {
        this.mq.removeEventListener('change', this.onMediaChange)
      } else {
        this.mq.removeListener(this.onMediaChange)
      }
    }

    this._clearTimer()
  }

  onClick(e) {
    if (!this._isEnabled()) return
    e.preventDefault()
    this.toggle()
  }

  onKeydown(e) {
    if (!this._isEnabled()) return

    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      this.toggle()
    }
  }

  onMediaChange() {
    const isOpen = this.root.getAttribute('data-accordion-open') === 'true'
    this._applyResponsiveState(this.root.classList.contains('is-open') || isOpen, true)
  }

  onTransitionEnd(e) {
    if (e.target !== this.panel || e.propertyName !== 'height') return

    const isOpen = this.root.classList.contains('is-open')

    this.panel.style.transition = ''
    this.panel.style.overflow = ''
    this.panel.style.willChange = ''
    this.panel.style.height = isOpen ? '' : '0px'
    this.panel.hidden = !isOpen
    this._isAnimating = false
  }

  toggle() {
    if (this._isAnimating) return
    if (!this._isEnabled()) return

    const nextOpen = !this.root.classList.contains('is-open')
    this._applyState(nextOpen, { skipAnim: false })
  }

  open() {
    if (this._isAnimating) return
    if (!this._isEnabled()) return

    this._applyState(true, { skipAnim: false })
  }

  close() {
    if (this._isAnimating) return
    if (!this._isEnabled()) return

    this._applyState(false, { skipAnim: false })
  }

  _isEnabled() {
    if (!this.isSpOnly) return true
    return this.mq.matches
  }

  _applyResponsiveState(isOpen, skipAnim = true) {
    if (this._isEnabled()) {
      this._applyState(isOpen, { skipAnim })
      return
    }

    this._clearTimer()
    this.root.classList.remove('is-open')
    this.trigger.setAttribute('aria-expanded', 'true')
    this.panel.hidden = false
    this.panel.style.height = ''
    this.panel.style.transition = ''
    this.panel.style.overflow = ''
    this.panel.style.willChange = ''
  }

  _applyState(open, { skipAnim }) {
    this._clearTimer()

    const reduced =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const anim = !skipAnim && !reduced && this.duration > 0

    this.root.classList.toggle('is-open', open)
    this.root.setAttribute('data-accordion-open', open ? 'true' : 'false')
    this.trigger.setAttribute('aria-expanded', open ? 'true' : 'false')

    if (!anim) {
      this.panel.style.transition = ''
      this.panel.style.overflow = ''
      this.panel.style.willChange = ''

      if (open) {
        this.panel.hidden = false
        this.panel.style.height = ''
      } else {
        this.panel.style.height = '0px'
        this.panel.hidden = true
      }
      return
    }

    this._isAnimating = true
    this.panel.hidden = false
    this.panel.style.overflow = 'hidden'
    this.panel.style.willChange = 'height'

    if (open) {
      this.panel.style.transition = 'none'
      this.panel.style.height = '0px'

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const target = this.panel.scrollHeight
          this.panel.style.transition = `height ${this.duration}ms cubic-bezier( 0.22, 1, 0.36, 1 )`
          this.panel.style.height = `${target}px`
        })
      })
    } else {
      const start = this.panel.scrollHeight

      this.panel.style.transition = 'none'
      this.panel.style.height = `${start}px`

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.panel.style.transition = `height ${this.duration}ms cubic-bezier( 0.22, 1, 0.36, 1 )`
          this.panel.style.height = '0px'
        })
      })
    }

    this._tid = window.setTimeout(() => {
      if (!this._isAnimating) return
      this.onTransitionEnd({ target: this.panel, propertyName: 'height' })
    }, this.duration + 100)
  }

  _getDuration() {
    const v = this.root.getAttribute('data-accordion-duration')
    const n = parseInt(v, 10)
    return Number.isFinite(n) ? n : 300
  }

  _clearTimer() {
    if (this._tid) {
      clearTimeout(this._tid)
      this._tid = 0
    }

    if (this.panel) {
      this.panel.style.transition = ''
      this.panel.style.overflow = ''
      this.panel.style.willChange = ''
    }

    this._isAnimating = false
  }
}
